/**
 * MaterialIcons — general material item icons.
 *
 * slime_gel / mushroom_spore / veteran_badge / return_scroll were upgraded
 * to the Coil Art Pack spec (docs/design/coil-art/ICON_DESIGN_SHEETS.md):
 * 40×40 viewBox, gradient shading instead of flat fills, no drop shadows.
 * pig_foot moved to MobDropIcons.ts. Hollow Coil drops live in
 * MobDropIcons.ts; quest items in QuestItemIcons.ts.
 */

function wrap40(size: number, content: string): string {
  return `<svg width="${size}" height="${size}" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">${content}</svg>`;
}

/** Tier 1 — a jiggly blob of green slime gel, translucent with a highlight bubble. */
function slimeGel(size: number = 32): string {
  return wrap40(size, `
    <defs>
      <linearGradient id="ci_sg_body" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stop-color="#32cd32"/>
        <stop offset="100%" stop-color="#2a8a2a"/>
      </linearGradient>
    </defs>
    <!-- Rounded blob (soft wobble on top suggests the jiggle) -->
    <path d="M7.5,25 Q6,13 13,9 Q16,7.2 19,7 Q20.5,4.5 22,7 Q25,7.2 27,8.8 Q34,13 32.5,25 Q31.8,33 20,33.5 Q8.2,33 7.5,25 Z"
      fill="url(#ci_sg_body)" opacity="0.9"/>
    <!-- Highlight bubble, top-left -->
    <ellipse cx="14" cy="14" rx="4.2" ry="3" fill="#7fff7f" opacity="0.5"/>
    <circle cx="24.5" cy="11.5" r="1.6" fill="#7fff7f" opacity="0.35"/>
  `);
}

/** A cluster of glowing orange spores with a faint aura. */
function mushroomSpore(size: number = 32): string {
  return wrap40(size, `
    <defs>
      <radialGradient id="ci_ms_glow" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stop-color="#ff6600" stop-opacity="0.3"/>
        <stop offset="100%" stop-color="#ff6600" stop-opacity="0"/>
      </radialGradient>
      <radialGradient id="ci_ms_spore" cx="35%" cy="30%" r="80%">
        <stop offset="0%" stop-color="#ffa500"/>
        <stop offset="100%" stop-color="#cc6600"/>
      </radialGradient>
    </defs>
    <!-- Aura -->
    <circle cx="20" cy="20" r="16" fill="url(#ci_ms_glow)"/>
    <!-- Spore cluster -->
    <circle cx="20" cy="19" r="6.5" fill="url(#ci_ms_spore)"/>
    <circle cx="12.5" cy="14.5" r="4.5" fill="url(#ci_ms_spore)"/>
    <circle cx="27.5" cy="15.5" r="4.5" fill="url(#ci_ms_spore)"/>
    <circle cx="14" cy="27" r="3.8" fill="url(#ci_ms_spore)"/>
    <circle cx="26" cy="26.5" r="3.8" fill="url(#ci_ms_spore)"/>
    <!-- Drifting glow dots -->
    <circle cx="8" cy="9" r="1.2" fill="#ffa500" opacity="0.5"/>
    <circle cx="31" cy="9.5" r="1" fill="#ffa500" opacity="0.45"/>
    <circle cx="20" cy="7" r="0.9" fill="#ffa500" opacity="0.4"/>
    <circle cx="33" cy="22" r="0.8" fill="#ffa500" opacity="0.35"/>
    <!-- Highlight on the big spore -->
    <circle cx="18" cy="16.5" r="1.6" fill="rgba(255,255,255,0.35)"/>
  `);
}

/** A metallic shield badge with a gold star inset and pin back. */
function badge(size: number = 32): string {
  return wrap40(size, `
    <defs>
      <linearGradient id="ci_vb_metal" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stop-color="#c0c0c0"/>
        <stop offset="100%" stop-color="#808080"/>
      </linearGradient>
    </defs>
    <!-- Pin back peeking over the top -->
    <rect x="13" y="3.2" width="14" height="3" rx="1.5" fill="#404040"/>
    <!-- Shield -->
    <path d="M20,5 L33,10 L33,20 Q33,30 20,36.5 Q7,30 7,20 L7,10 Z" fill="url(#ci_vb_metal)"/>
    <!-- Gold inset border -->
    <path d="M20,8 L30.4,12 L30.4,20 Q30.4,27.6 20,33.2 Q9.6,27.6 9.6,20 L9.6,12 Z"
      fill="none" stroke="#ffd700" stroke-width="1"/>
    <!-- Gold star -->
    <polygon points="20,12.5 21.9,17 26.7,17.3 23,20.5 24.1,25.2 20,22.6 15.9,25.2 17,20.5 13.3,17.3 18.1,17" fill="#ffd700"/>
    <!-- Metallic sheen -->
    <path d="M8.8,10.8 L19.4,6.7" stroke="rgba(255,255,255,0.3)" stroke-width="1.2" fill="none" stroke-linecap="round"/>
  `);
}

/** A rolled parchment scroll with a ribbon binding and red wax seal. */
function scroll(size: number = 32): string {
  return wrap40(size, `
    <defs>
      <linearGradient id="ci_rs_parch" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stop-color="#e8d8a0"/>
        <stop offset="100%" stop-color="#c8b880"/>
      </linearGradient>
    </defs>
    <!-- Rolled body -->
    <rect x="8" y="13" width="24" height="14" fill="url(#ci_rs_parch)"/>
    <!-- Roll ends (spiral hint inside) -->
    <ellipse cx="8" cy="20" rx="3.2" ry="7" fill="#c8b880" stroke="#a89860" stroke-width="0.7"/>
    <ellipse cx="8" cy="20" rx="1.4" ry="3.5" fill="#a89860" opacity="0.6"/>
    <ellipse cx="32" cy="20" rx="3.2" ry="7" fill="#c8b880" stroke="#a89860" stroke-width="0.7"/>
    <ellipse cx="32" cy="20" rx="1.4" ry="3.5" fill="#a89860" opacity="0.6"/>
    <!-- Parchment texture lines -->
    <path d="M11,17 L16,17 M24,23 L29,23" stroke="#a89860" stroke-width="0.7" opacity="0.5" fill="none"/>
    <!-- Ribbon binding -->
    <rect x="17" y="12" width="6" height="16" fill="#8b4513"/>
    <!-- Wax seal -->
    <circle cx="20" cy="20" r="4.6" fill="#cc3333"/>
    <circle cx="20" cy="20" r="2.8" fill="none" stroke="rgba(0,0,0,0.25)" stroke-width="0.8"/>
    <circle cx="20" cy="20" r="1" fill="rgba(0,0,0,0.2)"/>
  `);
}

function throwingStar(size: number = 32): string {
  return `<svg width="${size}" height="${size}" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
    <!-- 4-pointed star -->
    <polygon points="16,2 19,12 30,16 19,20 16,30 13,20 2,16 13,12" fill="#C0C0C0" stroke="rgba(0,0,0,0.25)" stroke-width="0.8"/>
    <!-- Inner detail -->
    <circle cx="16" cy="16" r="3" fill="#999" stroke="rgba(0,0,0,0.15)" stroke-width="0.5"/>
    <!-- Edge highlights -->
    <path d="M16,4 L18,12" stroke="rgba(255,255,255,0.3)" stroke-width="0.8"/>
    <path d="M28,16 L20,18" stroke="rgba(255,255,255,0.3)" stroke-width="0.8"/>
  </svg>`;
}

export const MATERIAL_ICONS: Record<string, (size?: number) => string> = {
  slime_gel: slimeGel,
  mushroom_spore: mushroomSpore,
  veteran_badge: badge,
  return_scroll: scroll,
  subi_throwing_star: throwingStar,
};
